import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatName = (name) => name.replace(/_/g, ' ');
const formatCategory = (cat) => cat.replace(/_/g, ' ');

const FeaturedCarousel = () => {
  const [products, setProducts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  const fetchFeaturedProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products/featured`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching featured products:', error);
    }
  }, []);

  useEffect(() => {
    fetchFeaturedProducts();
  }, [fetchFeaturedProducts]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % products.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + products.length) % products.length);
  };

  if (products.length === 0) return null;

  const currentProduct = products[currentIndex];
  const currentImages = currentProduct.images && currentProduct.images.length > 0 ? currentProduct.images : [];

  const getCategoryRoute = (category) => {
    if (category === 'woodwork_and_art') return '/woodwork-and-art';
    if (category === 'mandirs') return '/mandirs';
    if (category === 'copperware') return '/copperware';
    if (category === 'candles') return '/candles';
    return '/';
  };

  return (
    <div className="relative" data-testid="featured-carousel">
      <div className="bg-[#EBE5DA] border border-[#D7C0A0] p-8 md:p-12">
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div className="w-full md:w-1/2 flex items-center justify-center">
            <div className="h-[400px] w-full bg-[#D7C0A0]/30 flex items-center justify-center">
              {currentImages.length > 0 ? (
                <img 
                  src={currentImages[0]} 
                  alt={formatName(currentProduct.name)}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="w-full h-full hidden items-center justify-center text-[#8D7B68]">
                Featured Product
              </div>
            </div>
          </div>
          <div className="w-full md:w-1/2 space-y-4">
            <p className="text-sm tracking-widest uppercase text-[#8D7B68]" data-testid="carousel-category">
              {formatCategory(currentProduct.category)}
            </p>
            <h3 className="text-3xl md:text-4xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="carousel-product-name">
              {formatName(currentProduct.name)}
            </h3>
            <p className="text-base leading-relaxed text-[#5D4037]" data-testid="carousel-product-description">
              {currentProduct.description}
            </p>
            <div className="pt-4">
              <button
                onClick={() => navigate(getCategoryRoute(currentProduct.category))}
                className="btn-primary"
                data-testid="carousel-explore-button"
              >
                Explore Collection
              </button>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full hover:bg-white transition-colors"
        data-testid="carousel-prev-button"
      >
        <ChevronLeft className="w-6 h-6 text-[#2C1810]" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full hover:bg-white transition-colors"
        data-testid="carousel-next-button"
      >
        <ChevronRight className="w-6 h-6 text-[#2C1810]" />
      </button>

      <div className="flex justify-center gap-2 mt-4">
        {products.map((p, index) => (
          <button
            key={p.id}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === currentIndex ? 'bg-[#B87333]' : 'bg-[#D7C0A0]'
            }`}
            data-testid={`carousel-dot-${index}`}
          />
        ))}
      </div>
    </div>
  );
};

export default FeaturedCarousel;
